import { useState, useEffect } from 'react';
import {
    AdditionalCostCostSplitDetailsDto,
    AdditionalCostDetailsDto,
    ChildId,
    CostAllocationId,
    CostAllocationRefersTo,
} from '../../../../../../../types';
import { AppCardListItem, DetailsColumn, DetailsRow } from '../../../../../../atoms';
import { commonTexts, settlementTexts } from '../../../../../../../consts/text';
import { getFormattedPrice } from '../../../../../../../utils/getFormattedPrice';
import { payerMapper } from '../../../../../../../consts/settlementChildren/payerMapper';
import { If } from '../../../../../../atoms/If/If';
import { EditCostSplitModal } from '../../../../AddEditCostSplitModal/EditCostSplitModal/EditCostSplitModal';
import { UserDollar } from '../../../../../../../assets';
import { AppConfirmModal } from '../../../../../../organisms';
import { useToggle } from '../../../../../../../hooks/useToggle/useToggle';
import { useAppMutation } from '../../../../../../../services/reactQuery/useAppMutation';
import StaffHTTPService from '../../../../../../../services/HTTPService/staff/StaffHTTPService';
import { useSimpleNotification } from '../../../../../../../hooks/useSimpleNotification/useSimpleNotification';
import { AdditionalCostInvoice } from '../AdditionalCostDetails/AdditionalCostInvoice';

type Props = {
    detailsDto: AdditionalCostDetailsDto;
    record: AdditionalCostCostSplitDetailsDto;
    childId: ChildId;
    idx: number;
};

export const AdditionalCostSplitDetails = ({ detailsDto, record, idx, childId }: Props) => {
    const { displaySuccess, displayError } = useSimpleNotification();

    const [openEditCostSplitModal, setOpenEditCostSplitModal] = useState(false);
    const [verifyCostModalOpen, openVerifyCostModal, closeVerifyCostModal] = useToggle(false);

    const { mutate: removeCostAllocation, isLoading: isRemoveCostAllocationLoading } =
        useAppMutation(StaffHTTPService.settlements.removeCostAllocation, {
            key: ['REMOVE_COST_ALLOCATION'],
            onSuccess: () => {
                displaySuccess(settlementTexts.children.costAllocation.removeSuccess);
            },
            onError: () => {
                displayError(commonTexts.errorMessages.unknown);
            },
            invalidateQueryKeys: [
                ['ADDITIONAL_COST_DETAILS'],
                ['ADDITIONAL_COST_LIST'],
                ['ADDITIONAL_COST'],
                ['CHILD_SETTLEMENT_BALANCE'],
                ['COST_ALLOCATION'],
                ['CHILD_SETTLEMENT', childId],
                ['CHILD_SETTLEMENT_COST_SPLIT', childId],
            ],
        });

    const handleDeleteCostSplit = () => {
        removeCostAllocation({
            costAllocationId: record.id as CostAllocationId,
        });
    };

    const useMediaQuery = (query: string) => {
        const [matches, setMatches] = useState(window.matchMedia(query).matches);

        useEffect(() => {
            const media = window.matchMedia(query);
            const listener = () => setMatches(media.matches);
            media.addEventListener('change', listener);
            return () => media.removeEventListener('change', listener);
        }, [query]);

        return matches;
    };

    const isMobile = useMediaQuery('(max-width: 992px)');

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 16,
                    alignItems: 'flex-start',
                    width: '100%',
                }}
            >
                <div style={{ flex: 1 }}>
                    <AppCardListItem
                        key={detailsDto.id}
                        index={idx}
                        listItem={record}
                        options={
                            !detailsDto.verified
                                ? [
                                      {
                                          label: 'Edytuj',
                                          onClick: () => setOpenEditCostSplitModal(true),
                                      },
                                      { label: 'Usuń', onClick: openVerifyCostModal },
                                  ]
                                : undefined
                        }
                    >
                        <DetailsRow>
                            <DetailsColumn
                                title={settlementTexts.children.costSplit.institutionName}
                                span={16}
                            >
                                {record.foundationName}
                            </DetailsColumn>
                        </DetailsRow>

                        <DetailsRow>
                            <DetailsColumn title={settlementTexts.children.costSplit.nip} span={6}>
                                {record.nip}
                            </DetailsColumn>
                            <DetailsColumn
                                title={settlementTexts.children.costSplit.percentageAmount}
                                span={6}
                            >
                                {record.percentageAmount ? `${record.percentageAmount} %` : '-'}
                            </DetailsColumn>
                            <DetailsColumn
                                title={settlementTexts.children.costSplit.finalAmount}
                                span={6}
                            >
                                {getFormattedPrice(record.finalGrossAmount || 0)}
                            </DetailsColumn>
                            <DetailsColumn
                                title={settlementTexts.children.costSplit.payerType}
                                span={6}
                            >
                                {payerMapper[record.payerType]}
                            </DetailsColumn>
                        </DetailsRow>

                        <DetailsRow>
                            <DetailsColumn
                                title={settlementTexts.children.costSplit.refersTo}
                                span={6}
                            >
                                {record.sourceName ?? 'Inne'}
                            </DetailsColumn>
                            <If
                                condition={
                                    record.costCategory === CostAllocationRefersTo.EXTRA_LESSONS
                                }
                            >
                                <DetailsColumn
                                    title={settlementTexts.children.costSplit.courses}
                                    span={6}
                                >
                                    {record?.courseList?.map((c) => c.courseName).join(', ') ?? ''}
                                </DetailsColumn>
                            </If>
                            <DetailsColumn
                                title={settlementTexts.children.costSplit.invoiceNotes}
                                span={12}
                            >
                                {record.invoiceNotes}
                            </DetailsColumn>
                        </DetailsRow>
                    </AppCardListItem>
                </div>

                {record.invoiceData && (
                    <div
                        style={{
                            width: isMobile ? '100%' : 320,
                            flexShrink: 0,
                        }}
                    >
                        <AdditionalCostInvoice
                            invoiceData={record.invoiceData}
                            title="Rozliczenie"
                        />
                    </div>
                )}
            </div>

            <EditCostSplitModal
                type="additional-costs"
                isOpen={openEditCostSplitModal}
                onClose={() => setOpenEditCostSplitModal(false)}
                childId={childId}
                additionalCostId={detailsDto.id}
                costAllocationId={record.id as CostAllocationId}
            />

            <AppConfirmModal
                open={verifyCostModalOpen}
                width={420}
                title="Czy na pewno chcesz usunąć podział kosztów?"
                icon={<UserDollar />}
                confirmActionLoading={isRemoveCostAllocationLoading}
                okButtonProps={{ title: 'Usuń', danger: true }}
                cancelButtonProps={{ title: 'Anuluj' }}
                onOk={handleDeleteCostSplit}
                onCancel={closeVerifyCostModal}
            />
        </>
    );
};
